import React, { useRef } from "react";
import imageCompression from "browser-image-compression";
import { formatMessage } from "../../../translations/format-message";
import toast from "react-hot-toast";
import { getBase64 } from "../../../constants/imagePicker.helper";
import {
  MAX_IMAGE_UPLOAD_LIMIT,
  resizeImage,
} from "../../../constants/constants";
import {
  IAddMedicalImages,
  IAgreementRecordVM,
  IsAgreementImages,
  ISignStateProps,
} from "../../../models/response/ISMRResponse";
import { FormikErrors } from "formik";

interface Props {
  disabled?: boolean;
  setUploadAgreementImages: React.Dispatch<React.SetStateAction<IsAgreementImages>>;
  setCanvaSign: React.Dispatch<React.SetStateAction<ISignStateProps>>;
  selectedImages: IAddMedicalImages[];
  setAgreementImageUploader: (loaderStatus: boolean) => void;
  setFieldError: (field: string, message: string | undefined) => void;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<IAgreementRecordVM>>;
}

const SignImageUploader: React.FC<Props> = ({
  disabled = false,
  setUploadAgreementImages,
  setCanvaSign,
  selectedImages,
  setAgreementImageUploader,
  setFieldError,
  setFieldValue,
}) => {
  const agreementFileInputRef = useRef<HTMLInputElement | null>(null);

  const imageCompressOptions = {
    maxSizeMB: 0.5,
    maxWidthOrHeight: 200,
    useWebWorker: true,
    fileType: "image/webp",
  };

  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (!files.length) return;

    if (files.length + selectedImages.length > MAX_IMAGE_UPLOAD_LIMIT) {
      toast.error(formatMessage("upload_up_to_10_images_only"));
      return;
    }

    try {
      setAgreementImageUploader(true);

      const validImageFiles = files.filter((file) =>
        file.type.startsWith("image/")
      );

      const medicalImagesDetails = [...selectedImages];

      for (let i = 0; i < validImageFiles.length; i++) {
        const file = validImageFiles[i];
        const base64Image = await resizeImage(file, 1600, 1600, 0.5);
        const thumbnailBase64 = await resizeImage(file, 200, 200, 0.1);

        medicalImagesDetails.push({
          ImageType: 4,
          ImageSource: file.name,
          ImageBase64: base64Image,
          OrderId: selectedImages.length + i,
          ThumbnailImage: thumbnailBase64,
        });
      }

      if (medicalImagesDetails.length > 0) {
        setUploadAgreementImages({
          Images: medicalImagesDetails,
          IsUploaded: true,
        });
        setCanvaSign({ Sign: "", IsSignUploaded: false });
      }

      await setFieldValue("AgreementId", -1);
      await setFieldValue("Agreement", "");
      setFieldError("AgreementId", undefined);
    } catch (error) {
      toast.error(formatMessage("err_upload_image"));
    } finally {
      setAgreementImageUploader(false);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || disabled) return;

    try {
      const compressedFile = await imageCompression(
        event.target.files[0],
        imageCompressOptions
      );
      const base64: any = (await getBase64(compressedFile)) as string;

      setUploadAgreementImages({ Images: base64, IsUploaded: true });
      setCanvaSign({ Sign: "", IsSignUploaded: false });
    } catch {
      toast.error(formatMessage("err_upload_image"));
    }
  };

  const handleButtonClick = () => {
    if (selectedImages.length >= MAX_IMAGE_UPLOAD_LIMIT) {
      toast.error(formatMessage("maximum_10_images_allowed"));
      return;
    }

    if (!disabled && agreementFileInputRef.current) {
      agreementFileInputRef.current.value = ""; // Reset the file input
      agreementFileInputRef.current.click();
    }
  };

  const clearAgreementImages = () => {
    setUploadAgreementImages({ Images: [], IsUploaded: false });
    setCanvaSign({ Sign: "", IsSignUploaded: false });
  };

  return (
    <div>
      <input
        id="imguploadAgreement"
        name="file"
        type="file"
        accept="image/*"
        multiple
        ref={agreementFileInputRef}
        style={{ display: "none" }}
        disabled={disabled}
        onChange={handleImageSelect}
      />
      <button
        className="btn btn-primary"
        type="button"
        onClick={handleButtonClick}
        disabled={disabled}
      >
        <i className="bi bi-cloud-upload-fill"></i>{" "}
        {formatMessage("upload_image")}
      </button>
      {selectedImages?.length > 0 && (
        <button
          className="btn btn-outline-danger ms-2"
          type="button"
          onClick={clearAgreementImages}
          disabled={disabled}
        >
          <i className="bi bi-x-circle-fill me-1"></i>{" "}
          {formatMessage("clear_images")}
        </button>
      )}
    </div>
  );
};

export default SignImageUploader;
